import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
import { Container, Section, Kicker, Pill, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { SoftGate } from '../components/sections/SoftGate';
import { getArticle, ARTICLES } from '../data/insights';
import { SERVICE_LINES } from '../data/services';
import { fetchReaderUnlocked, setReaderUnlocked } from '../lib/leads';
import { usePortalAuth } from '../context/PortalAuth';
import { NotFound } from './NotFound';
export function ArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = getArticle(slug ?? '');
  const {
    user
  } = usePortalAuth();
  const reduced = useReducedMotion();
  const [unlocked, setUnlocked] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    void fetchReaderUnlocked().then(value => {
      if (active) setUnlocked(value);
    });
    return () => {
      active = false;
    };
  }, [slug]);
  if (!article) return <NotFound data-id="element-436" data-mp="27yduhs8umsll" />;
  const gateApplies = article.gated && !unlocked && !user;
  const teaser = article.body.slice(0, article.teaserCut);
  const remainder = article.body.slice(article.teaserCut);
  const related = ARTICLES.filter(item => item.slug !== article.slug).slice(0, 3);
  const lineName = SERVICE_LINES.find(line => line.id === article.serviceLine)?.name;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author.name,
      jobTitle: article.author.role
    },
    publisher: {
      '@type': 'Organization',
      name: 'Cavalla Solution',
      url: 'https://cavallasolution.com'
    },
    articleSection: lineName,
    about: article.sectors
  };
  const onUnlock = () => {
    setReaderUnlocked();
    setUnlocked(true);
  };
  return <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} data-id="element-437" data-mp="35merww9chy7y" />

      <section className="surface-dark" data-id="element-438" data-mp="3ajezxcpr9b1p">
        <Container className="py-12 sm:py-16" data-id="element-439" data-mp="3aidcs9vgsy3m">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-440" data-mp="3fchiyqwwp23n">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-441" data-mp="3kajdc7zs8nuf">
              <li data-id="element-442" data-mp="3kcdowvp5z3lu"><Link to="/" className="hover:text-white" data-id="element-443" data-mp="3kcdncr9imvjq">Home</Link></li>
              <li aria-hidden="true" data-id="element-444" data-mp="2moc1cd15ft81"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-445" data-mp="2mp57pcw74gpn" /></li>
              <li data-id="element-446" data-mp="2rmn8gg027pmk"><Link to="/insights" className="hover:text-white" data-id="element-447" data-mp="2rmdsll8qayul">Insights</Link></li>
              <li aria-hidden="true" data-id="element-448" data-mp="2wjx4iehyezjd"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-449" data-mp="2wjdpp6nlor9k" /></li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-450" data-mp="31kbew3712lkk">{article.title}</li>
            </ol>
          </nav>
          <Kicker data-id="element-451" data-mp="bty0uh7xzcmk">{lineName}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[30px] leading-tight text-white sm:text-[42px]" data-id="element-452" data-mp="bx2ahu46f0km">
            {article.title}
          </h1>
          <p className="mt-6 text-small text-white/55" data-id="element-453" data-mp="21atl0bgijqdr">
            {article.author.name}, {article.author.role} ·{' '}
            {new Date(article.publishedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}{' '}
            · {article.readTime} minute read
          </p>
        </Container>
      </section>

      <Container className="py-10 sm:py-14" data-id="element-454" data-mp="1hox6i2dp38li">
        <img src={article.heroImage} alt={article.heroAlt} className="aspect-[16/7] w-full rounded-md object-cover" data-id="element-455" data-mp="1hlj5oykoj7g6" />

        <div className="mt-8 flex flex-wrap gap-2" data-id="element-456" data-mp="18nz23izcgz2e">
          {article.sectors.map(sector => <Pill key={sector} data-id="element-457" data-mp="13pecselmrzsq">{sector}</Pill>)}
        </div>

        <div className="mt-10 max-w-prose" data-id="element-458" data-mp="331qj53k06l7v">
          <Callout data-id="element-459" data-mp="330fhb9a4bnje">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-460" data-mp="32yun5krpu6m2">In short</p>
            <p className="mt-2 text-[15px] text-body" data-id="element-461" data-mp="2y0b0tj97mst6">{article.summaryBlock}</p>
          </Callout>
        </div>

        <article className="mt-10 max-w-prose" data-id="element-462" data-mp="1zq1bvsgokjlb">
          {teaser.map((section, index) => <section key={index} data-id="element-463" data-mp="1lqifty158ypd" data-mpi={index}>
              {section.heading && <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink first:mt-0" data-id="element-464" data-mp="1lsmi6bje9pnj">
                  {section.heading}
                </h2>}
              {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="mb-5 text-[17px] leading-[1.7] text-body" data-id="element-465" data-mp="1grysbt6wjeuw" data-mpi={pIndex}>
                  {paragraph}
                </p>)}
            </section>)}

          {/* The remaining content stays in the served markup for indexing. The gate is a client side reveal only. */}
          <div className={gateApplies ? 'pointer-events-none max-h-64 select-none overflow-hidden' : ''} aria-hidden={gateApplies} data-id="element-466" data-mp="t1cneas72ap4">
            {remainder.map((section, index) => <motion.section key={index} initial={reduced || gateApplies ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut',
            delay: index * 0.03
          }} data-id="element-467" data-mp="eevzzym8qysv" data-mpi={index}>
                {section.heading && <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink" data-id="element-468" data-mp="jd79g88mj7ak">{section.heading}</h2>}
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="mb-5 text-[17px] leading-[1.7] text-body" data-id="element-469" data-mp="tbdbcpufkdff" data-mpi={pIndex}>
                    {paragraph}
                  </p>)}
              </motion.section>)}
          </div>

          {gateApplies && <SoftGate article={article} onUnlock={onUnlock} data-id="element-470" data-mp="125i5pj8rw6zp" />}
        </article>

        {!gateApplies && <div className="mt-12 max-w-prose rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-471" data-mp="1gzwl79t4oy9m">
            <h2 className="font-display text-[22px] text-ink" data-id="element-472" data-mp="1c0te7jtl8hz0">Working on something similar</h2>
            <p className="mt-3 text-[15px] text-body" data-id="element-473" data-mp="1j419c35rby1c">
              A discovery call lasts thirty minutes and costs nothing. Tell us the decision in front of you.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-474" data-mp="1t2pfo4odqop7">
              <Button to="/contact" data-id="element-475" data-mp="1xva6cxbzoloi">Book a discovery call</Button>
              <Button to="/assessment" variant="secondary" data-id="element-476" data-mp="1xwtq4xwutev1">Take the assessment</Button>
            </div>
          </div>}
      </Container>

      <Section tone="mist" aria-labelledby="related-articles" data-id="element-477" data-mp="26unjbzzbnsdz">
        <h2 id="related-articles" className="font-display text-[26px] text-ink" data-id="element-478" data-mp="2burxhjcrcb11">
          Related reading
        </h2>
        <div className="mt-8 grid gap-8 sm:grid-cols-3" data-id="element-479" data-mp="2gu4lw8ja1q5q">
          {related.map(item => <article key={item.slug} data-id="element-480" data-mp="hha6tnh22y1s">
              <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-481" data-mp="hibls31704os">{item.sectors[0]}</p>
              <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-482" data-mp="mgddfccopcbv">
                <Link to={`/insights/${item.slug}`} className="underline-offset-4 hover:underline" data-id="element-483" data-mp="ex46dprnhppz">
                  {item.title}
                </Link>
              </h3>
              <p className="mt-3 text-[15px] text-body" data-id="element-484" data-mp="55zsyuhmicx9">{item.excerpt}</p>
            </article>)}
        </div>
      </Section>
    </>;
}